import { ReportUploadPanel } from "@/components/report_upload_panel"

export const HEB_PORTAL_URL = "https://avp.heb.com/"

/** What `POST /integration/heb/upload` answers with. */
export interface HebUploadResponse {
  job_id: string
  /** Payments in the file that tie out and will be booked. */
  payments: number
  /** Payments refused because the export did not carry all of their rows. */
  refused_payments: string[]
}

export function heb_upload_summary(response: HebUploadResponse): { title: string; description: string } {
  const booked = `${response.payments} payment${response.payments === 1 ? "" : "s"} will be booked in the background.`
  if (response.refused_payments.length === 0) {
    return { title: "Upload received", description: booked }
  }
  return {
    title: "Upload received — some payments were skipped",
    description: `${booked} Skipped because their rows do not add up to the payment amount: ${response.refused_payments.join(
      ", "
    )}. Export again with Export All and re-upload; nothing is booked twice.`,
  }
}

interface HebUploadSectionProps {
  /** Named by admin-style callers; a brand uploads to its own org and may omit it. */
  integrationId?: string
  onUploaded?: () => void
  className?: string
}

/**
 * Upload the AP-AR Application's payment export, for brands whose H-E-B payment advices are not
 * emailed and who have not saved their portal login (with it, the export is pulled automatically).
 */
export function HebUploadSection({ integrationId, onUploaded, className }: HebUploadSectionProps) {
  return (
    <ReportUploadPanel<HebUploadResponse>
      className={className}
      endpoint="/api/integration/heb/upload"
      fields={integrationId ? { integration_id: integrationId } : undefined}
      accept={{ "text/csv": [".csv"] }}
      dropLabel="Drag and drop the payment_detail export"
      formatsLabel=".csv"
      successMessage={heb_upload_summary}
      onUploaded={() => onUploaded?.()}>
      <div class="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
        <p>Pull the payment report from H-E-B's AP-AR Application:</p>
        <ol class="list-decimal list-outside space-y-1 ml-5">
          <li>
            Log in at{" "}
            <a href={HEB_PORTAL_URL} target="_blank" rel="noopener noreferrer" class="underline hover:text-blue-700">
              {HEB_PORTAL_URL}
            </a>
            . (Save your portal login above and we'll pull this for you every weekday instead.)
          </li>
          <li>
            Click <strong>Search</strong>, and under <strong>Record type…</strong> choose{" "}
            <strong>Payments from HEB</strong>.
          </li>
          <li>
            Under <strong>Occurred…</strong> set the date range — <strong>this is the important part</strong>.{" "}
            <strong>Last 30 days</strong> is right for a regular upload; use <strong>Custom range…</strong> to reach
            back to the last payment you uploaded (or further, the first time). Overlapping a range you already uploaded
            is fine — nothing is booked twice.
          </li>
          <li>
            Press <strong>Export All</strong> at the top right of the results. Don't tick individual payments or use{" "}
            <strong>Export</strong> — a payment missing some of its rows won't add up, and we skip it.
          </li>
          <li>
            Wait for the download to appear — the portal takes a moment to build it, and it is also listed under{" "}
            <strong>My Downloads</strong>. Click it to save the file (named like{" "}
            <code class="text-xs">payment_detail_…csv</code>).
          </li>
          <li>Upload that file here.</li>
        </ol>
        <p class="text-xs text-blue-800 italic">
          Every invoice paid and every bill H-E-B netted out (coupons, e-commerce, shortages, unsaleables…) becomes a
          transaction on its payment. We only book payments whose rows add up to the payment amount.
        </p>
      </div>
    </ReportUploadPanel>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("heb_upload_summary", () => {
    it("says how many payments will be booked", () => {
      const summary = heb_upload_summary({ job_id: "j", payments: 1, refused_payments: [] })
      expect(summary.title).toBe("Upload received")
      expect(summary.description).toBe("1 payment will be booked in the background.")
      expect(heb_upload_summary({ job_id: "j", payments: 26, refused_payments: [] }).description).toContain(
        "26 payments"
      )
    })
    it("names the payments the export cut short", () => {
      const summary = heb_upload_summary({ job_id: "j", payments: 25, refused_payments: ["0004212897"] })
      expect(summary.title).toContain("skipped")
      expect(summary.description).toContain("0004212897")
      expect(summary.description).toContain("Export All")
    })
  })
}
